/**
 * Unit conversions keyed by the unit token authored in the cm chart.
 *   factor   — multiply the cm/gram value by this to get the target value
 *   decimals — rounding for the converted value
 *   to       — the relabelled unit shown in the inch view
 */
const UNITS = {
  cm: { to: 'in', factor: 1 / 2.54, decimals: 1 },
  g: { to: 'lb', factor: 1 / 453.59237, decimals: 2 },
  kg: { to: 'lb', factor: 2.2046226218, decimals: 1 },
};

const UNIT_TOKENS = Object.keys(UNITS).sort((a, b) => b.length - a.length).join('|');
const PAREN_UNIT_RE = new RegExp(`([(（])\\s*(${UNIT_TOKENS})\\s*([)）])`, 'gi');
const TEXT_UNIT_RE = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${UNIT_TOKENS})\\b`, 'gi');

/**
 * Derives an inch size chart from the cm chart authored by the brand, so the
 * CM/IN toggle works from a single source with no duplicate data entry.
 *
 * Conversion rule:
 *   - The first cell of every row is the size-label axis (`1`, `2`, `0`, `-`,
 *     "Free size"…) and is never converted, even when it is numeric.
 *   - The header row (and any `<th>`) is never value-converted.
 *   - Each column's unit is read from its header cell (e.g. "Weight (g)" -> g,
 *     "Case (cm)" -> cm); columns with no explicit unit default to cm. Numeric
 *     tokens are then converted with that column's rule (cm -> in, g/kg -> lb).
 *   - Unit captions/headings ("unit（cm）", "(g)"…) are relabelled to match.
 *   - Any element flagged `data-unit-convert` (e.g. the model_data line) has its
 *     `<number><unit>` measurements converted too.
 * If a chart has no convertible measurement (e.g. a "Free size" only chart),
 * no inch view is built and the toggle never appears.
 */
export default class SizeChart {
  /**
   * @param {HTMLElement} root              container holding the cm source
   * @param {Object}      [options]
   * @param {HTMLElement} [options.toggleHost]  where the CM/IN toggle is appended (defaults to root)
   */
  constructor(root, options = {}) {
    this.root = root;
    if (!this.root) return;

    // Toggle disabled for this product type via theme settings.
    if (this.root.getAttribute('data-unit-toggle') === 'off') return;

    this.cmSource = this.root.querySelector('[data-unit-content="cm"]');
    if (!this.cmSource || !this.cmSource.querySelector('table')) return;

    this.toggleHost = options.toggleHost || this.root;

    this.inSource = this.buildInch(this.cmSource);
    if (!this.inSource) return; // nothing convertible -> stay in cm, no toggle

    this.inSource.setAttribute('data-unit-content', 'in');
    this.inSource.style.display = 'none';
    this.cmSource.insertAdjacentElement('afterend', this.inSource);

    this.extras = this.buildExtras();

    this.buildToggle();
    this.setUnit('cm');
  }

  /**
   * Clones the cm content and converts every measurement cell to its target
   * unit, column by column.
   * @returns {HTMLElement|null} the converted clone, or null if nothing changed.
   */
  buildInch(source) {
    const clone = source.cloneNode(true);
    let converted = false;

    clone.querySelectorAll('table').forEach((table) => {
      const rows = Array.from(table.querySelectorAll('tr'));
      if (!rows.length) return;

      const units = this.columnUnits(rows[0]);

      rows.forEach((row, rowIndex) => {
        if (rowIndex === 0) return; // header row: relabelled, never value-converted
        const cells = row.children;
        for (let i = 0; i < cells.length; i += 1) {
          const cell = cells[i];
          if (i === 0 || cell.tagName === 'TH') continue; // size-label axis / headers
          if (this.convertCell(cell, units[i] || 'cm')) converted = true;
        }
      });
    });

    this.relabelUnits(clone);

    return converted ? clone : null;
  }

  /**
   * Reads the per-column unit from a table's header row.
   * @returns {string[]} unit token (e.g. "cm", "g") or null per column index.
   */
  columnUnits(headerRow) {
    return Array.from(headerRow.children).map((cell) => {
      const match = cell.textContent.match(new RegExp(`[(（]\\s*(${UNIT_TOKENS})\\s*[)）]`, 'i'));
      return match ? match[1].toLowerCase() : null;
    });
  }

  /**
   * Builds an inch twin for any free-text element flagged `data-unit-convert`
   * (e.g. the model_data line), converting only `<number><unit>` measurements.
   * @returns {{cm: HTMLElement, in: HTMLElement}[]}
   */
  buildExtras() {
    const extras = [];
    this.root.querySelectorAll('[data-unit-convert]').forEach((cm) => {
      const inClone = cm.cloneNode(true);
      if (!this.convertFreeText(inClone)) return;
      inClone.removeAttribute('data-unit-convert');
      inClone.style.display = 'none';
      cm.insertAdjacentElement('afterend', inClone);
      extras.push({ cm, in: inClone });
    });
    return extras;
  }

  /**
   * Swaps the textual unit for its target ("(cm)" -> "(in)", "(g)" -> "(lb)")
   * in captions and headings, leaving measurement values and ordinary words
   * untouched.
   */
  relabelUnits(clone) {
    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const next = node.nodeValue.replace(
          PAREN_UNIT_RE,
          (full, open, unit, close) => open + UNITS[unit.toLowerCase()].to + close
        );
        if (next !== node.nodeValue) node.nodeValue = next;
      } else {
        node.childNodes.forEach(walk);
      }
    };
    walk(clone);

    // Fallback for a bare unit caption with no parentheses (e.g. "unit cm").
    clone.querySelectorAll('._unit').forEach((el) => {
      el.textContent = el.textContent.replace(
        new RegExp(`\\b(${UNIT_TOKENS})\\b`, 'gi'),
        (unit) => UNITS[unit.toLowerCase()].to
      );
    });
  }

  /**
   * Converts the bare numeric tokens inside a table cell's text nodes in place,
   * using the supplied column unit.
   * @returns {boolean} true if any value was converted.
   */
  convertCell(cell, unit) {
    const conv = UNITS[unit] || UNITS.cm;
    let changed = false;

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const next = this.normalizeDigits(node.nodeValue).replace(/\d+(?:\.\d+)?/g, (n) => {
          changed = true;
          return (parseFloat(n) * conv.factor).toFixed(conv.decimals);
        });
        if (next !== node.nodeValue) node.nodeValue = next;
      } else {
        node.childNodes.forEach(walk);
      }
    };

    walk(cell);
    return changed;
  }

  /**
   * Converts only `<number><unit>` measurements in free text (e.g. "170cm"),
   * leaving unitless numbers ("size 2") alone.
   * @returns {boolean} true if any value was converted.
   */
  convertFreeText(el) {
    let changed = false;

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const next = this.normalizeDigits(node.nodeValue).replace(TEXT_UNIT_RE, (full, num, unit) => {
          const conv = UNITS[unit.toLowerCase()];
          changed = true;

          return `${(parseFloat(num) * conv.factor).toFixed(conv.decimals)} ${conv.to}`;
        });
        if (next !== node.nodeValue) node.nodeValue = next;
      } else {
        node.childNodes.forEach(walk);
      }
    };

    walk(el);
    return changed;
  }

  /** Normalises full-width digits (０-９) to ASCII so they can be matched. */
  normalizeDigits(value) {
    return value.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));
  }

  buildToggle() {
    this.toggle = document.createElement('div');
    this.toggle.className = '_unitToggle';
    this.toggle.setAttribute('role', 'group');
    this.toggle.setAttribute('aria-label', 'Size unit');
    this.toggle.innerHTML =
      '<button type="button" class="_unitToggle__btn is-active" data-unit="cm" aria-pressed="true">CM</button>' +
      '<button type="button" class="_unitToggle__btn" data-unit="in" aria-pressed="false">IN</button>';

    this.btns = this.toggle.querySelectorAll('._unitToggle__btn');
    this.btns.forEach((btn) => {
      btn.addEventListener('click', () => this.setUnit(btn.getAttribute('data-unit')));
    });

    this.toggleHost.appendChild(this.toggle);
  }

  setUnit(unit) {
    const showCm = unit === 'cm';

    this.cmSource.style.display = showCm ? '' : 'none';
    this.inSource.style.display = showCm ? 'none' : '';

    (this.extras || []).forEach(({ cm, in: inEl }) => {
      cm.style.display = showCm ? '' : 'none';
      inEl.style.display = showCm ? 'none' : '';
    });

    this.btns.forEach((btn) => {
      const active = btn.getAttribute('data-unit') === unit;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }
}
